import React, { useCallback, useEffect, useState } from 'react';
import { Percent } from 'lucide-react';
import apiService from '../services/api';
import { AffiliateCommissionRate, CommissionBasis } from '../types';

const LEVEL_LABEL: Record<string, string> = {
  '1': 'Launch',
  '2': 'Elevate',
  '3': 'Dominate',
  all: 'Semua paket',
};

const BASIS_LABEL: Record<CommissionBasis, string> = {
  monthly_price: 'harga per bulan',
  invoice_total: 'total invoice',
};

/**
 * Tarif komisi khusus seorang Referrer.
 *
 * Kosong berarti dia mengikuti tarif program — itu keadaan yang normal, bukan
 * kekurangan. Baris di sini adalah kesepakatan khusus yang menang atas tarif
 * program dan tidak ikut berubah saat tarif program diubah.
 */
const AffiliateCommissionRatesPanel: React.FC<{ affiliateId: string }> = ({ affiliateId }) => {
  const [rates, setRates] = useState<AffiliateCommissionRate[]>([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<string | null>(null);

  const [level, setLevel] = useState<string>('2');
  const [basis, setBasis] = useState<CommissionBasis>('monthly_price');
  const [type, setType] = useState<'percent' | 'fixed'>('percent');
  const [value, setValue] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await apiService.getAffiliateCommissionRates(affiliateId);
      setRates(res.data ?? []);
    } catch {
      setRates([]);
    } finally {
      setLoading(false);
    }
  }, [affiliateId]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);
    setSaving(true);

    try {
      await apiService.setAffiliateCommissionRate(affiliateId, {
        subscriptionLevel: level === 'all' ? null : Number(level),
        basis,
        type,
        value: Number(value),
        reason: reason.trim(),
      });
      setValue('');
      setReason('');
      setMessage('Tarif tersimpan. Perubahannya tercatat di log audit.');
      load();
    } catch (err: any) {
      setMessage(err?.response?.data?.message ?? 'Gagal menyimpan tarif.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white shadow rounded-lg">
      <div className="px-4 py-5 sm:px-6 flex items-center gap-2">
        <Percent className="h-5 w-5 text-gray-400" />
        <div>
          <h3 className="text-lg leading-6 font-medium text-gray-900">Tarif Komisi Khusus</h3>
          <p className="text-sm text-gray-500">
            Kosong berarti Referrer ini mengikuti tarif program. Baris di sini menang atas tarif program
            dan tidak ikut berubah saat tarif program diubah.
          </p>
        </div>
      </div>

      <div className="border-t border-gray-200 px-4 py-4 space-y-4">
        {loading ? (
          <p className="text-sm text-gray-500">Memuat...</p>
        ) : rates.length === 0 ? (
          <p className="text-sm text-gray-500">Belum ada tarif khusus — mengikuti tarif program.</p>
        ) : (
          <ul className="divide-y divide-gray-200">
            {rates.map((rate) => (
              <li key={rate.id} className="py-2 flex items-center justify-between text-sm">
                <span className="font-medium text-gray-900">
                  {LEVEL_LABEL[rate.subscriptionLevel === null ? 'all' : String(rate.subscriptionLevel)]
                    ?? `Level ${rate.subscriptionLevel}`}
                </span>
                <span className="text-gray-700">
                  {rate.type === 'percent'
                    ? `${rate.value}% dari ${BASIS_LABEL[rate.basis] ?? rate.basis}`
                    : `Rp${Number(rate.value).toLocaleString('id-ID')} tetap`}
                </span>
              </li>
            ))}
          </ul>
        )}

        {message && (
          <div className="rounded-md bg-blue-50 border border-blue-200 p-3 text-sm text-blue-800">
            {message}
          </div>
        )}

        <form onSubmit={handleSave} className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end border-t pt-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Paket</label>
            <select value={level} onChange={(e) => setLevel(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm">
              <option value="1">Launch</option>
              <option value="2">Elevate</option>
              <option value="3">Dominate</option>
              <option value="all">Semua paket</option>
            </select>
          </div>
          <div>
            {/* Wajib dan tanpa default di backend: persen yang sama pada dasar
                berbeda menghasilkan komisi berbeda 12x untuk langganan tahunan. */}
            <label className="block text-xs font-medium text-gray-700 mb-1">Dihitung dari</label>
            <select value={basis} onChange={(e) => setBasis(e.target.value as CommissionBasis)}
              className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm">
              <option value="monthly_price">Harga per bulan</option>
              <option value="invoice_total">Total invoice</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Jenis</label>
            <select value={type} onChange={(e) => setType(e.target.value as 'percent' | 'fixed')}
              className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm">
              <option value="percent">Persen</option>
              <option value="fixed">Nominal tetap</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              {type === 'percent' ? 'Persen' : 'Nominal'}
            </label>
            <input type="number" step="0.01" min="0" required value={value}
              onChange={(e) => setValue(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm" />
          </div>
          <div className="md:col-span-4">
            <label className="block text-xs font-medium text-gray-700 mb-1">Alasan (wajib)</label>
            <input type="text" required value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder="Mis. kesepakatan khusus mitra komunitas"
              className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm" />
          </div>
          <div>
            <button type="submit" disabled={saving}
              className="w-full px-3 py-1.5 rounded-md text-sm text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50">
              {saving ? 'Menyimpan...' : 'Simpan Tarif'}
            </button>
          </div>
        </form>

        {type === 'percent' && basis === 'invoice_total' && (
          <p className="text-xs text-yellow-700 bg-yellow-50 border border-yellow-200 rounded p-2">
            Dihitung dari total invoice: langganan 12 bulan akan menghasilkan komisi kira-kira dua belas kali
            lipat dibanding langganan bulanan. Pilih "harga per bulan" kalau yang kamu maksud satu bulan saja.
          </p>
        )}
      </div>
    </div>
  );
};

export default AffiliateCommissionRatesPanel;
